import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  anchorPathOf,
  cityKeyAfterChoosing,
  groupPickupLocations,
  PickupCatalogLocation,
  PickupStateGroup,
  pickupPathOf,
  searchPickupLocations,
} from '../../utils/pickupLocationGroups';

export interface PickerNavigation<T extends PickupCatalogLocation> {
  groups: PickupStateGroup<T>[];
  stateKey: string | null;
  cityKey: string | null;
  state: PickupStateGroup<T> | null;
  city: PickupStateGroup<T>['cities'][number] | null;
  query: string;
  results: T[];
  searching: boolean;
  setQuery: (query: string) => void;
  chooseState: (key: string) => void;
  chooseCity: (key: string) => void;
  /** Back one level; a state with one city goes straight back to the state list. */
  back: () => void;
  goToStates: () => void;
  goToState: () => void;
}

/**
 * Spec 022: which state and city the picker shows. Starts on the chosen
 * location (FR-017), else the anchor (the pickup, for the return picker,
 * FR-019), else the state list. Mounted only while the picker is open.
 */
export function usePickerNavigation<T extends PickupCatalogLocation>(
  locations: readonly T[],
  valueId: number | null,
  anchor: PickupCatalogLocation | null | undefined,
): PickerNavigation<T> {
  const groups = useMemo(() => groupPickupLocations(locations), [locations]);
  const startPath = () => pickupPathOf(groups, valueId) ?? anchorPathOf(groups, anchor);
  const [path, setPath] = useState<{ stateKey: string | null; cityKey: string | null }>(
    () => startPath() ?? { stateKey: null, cityKey: null },
  );
  const [query, setQuery] = useState('');

  // The list may arrive after the picker opened: place it once it does.
  const placed = useRef(groups.length > 0);
  useEffect(() => {
    if (placed.current || groups.length === 0) return;
    placed.current = true;
    const start = startPath();
    if (start) setPath(start);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const state = groups.find(group => group.key === path.stateKey) ?? null;
  const city = state?.cities.find(group => group.key === path.cityKey) ?? null;
  const results = useMemo(() => searchPickupLocations(locations, query), [locations, query]);

  const chooseState = useCallback(
    (key: string) =>
      setPath({
        stateKey: key,
        cityKey: cityKeyAfterChoosing(groups.find(group => group.key === key)),
      }),
    [groups],
  );
  const chooseCity = useCallback(
    (key: string) => setPath(current => ({ ...current, cityKey: key })),
    [],
  );
  const goToStates = useCallback(() => setPath({ stateKey: null, cityKey: null }), []);
  const goToState = useCallback(() => setPath(current => ({ ...current, cityKey: null })), []);
  const back = useCallback(() => {
    if (city && state && state.cities.length > 1) {
      goToState();
    } else {
      goToStates();
    }
  }, [city, state, goToState, goToStates]);

  return {
    groups,
    stateKey: state ? path.stateKey : null,
    cityKey: city ? path.cityKey : null,
    state,
    city,
    query,
    results,
    searching: query.trim().length > 0,
    setQuery,
    chooseState,
    chooseCity,
    back,
    goToStates,
    goToState,
  };
}
